import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as api from '../services/api.js'
import useAdminLink from '../hooks/useAdminLink'

const MOBILE_BREAKPOINT = 768

export default function NotificationBell() {
  const { t } = useTranslation()
  const adminLink = useAdminLink()
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT)
  const dropdownRef = useRef(null)
  const notifRequested = useRef(false)
  const origTitle = useRef(document.title)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Request browser notification permission on first bell click
  const requestNotifPermission = () => {
    if (notifRequested.current) return
    notifRequested.current = true
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }

  // Fetch initial unread notifications
  const fetchUnread = useCallback(async () => {
    try {
      const data = await api.getNotifications({ unreadOnly: true })
      setNotifications(data.results.slice(0, 5))
      setUnreadCount(data.count)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    fetchUnread()
  }, [fetchUnread])

  // SSE connection
  useEffect(() => {
    const token = api.getSessionToken()
    if (!token) return
    const es = new EventSource(`/api/admin/events?token=${token}`)
    es.addEventListener('notification', (e) => {
      try {
        const notif = JSON.parse(e.data)
        setUnreadCount(prev => prev + 1)
        setNotifications(prev => [notif, ...prev].slice(0, 5))
        if ('Notification' in window && Notification.permission === 'granted' && document.visibilityState === 'hidden') {
          const link = getLink(notif)
          const n = new Notification(notif.type.toUpperCase(), {
            body: notif.message,
            icon: '/favicon.ico',
          })
          n.onclick = () => {
            window.focus()
            window.location.href = link
            n.close()
          }
        }
      } catch { /* ignore */ }
    })
    es.addEventListener('error', () => {
      // will auto-reconnect
    })
    return () => es.close()
  }, [])

  // Update tab title with unread count
  useEffect(() => {
    document.title = unreadCount > 0
      ? `(${unreadCount}) ${origTitle.current}`
      : origTitle.current
    return () => { document.title = origTitle.current }
  }, [unreadCount])

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  const handleMarkRead = async (id) => {
    try {
      await api.markNotificationRead(id)
      setNotifications(prev => prev.filter(n => n.objectId !== id))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch { /* ignore */ }
  }

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead()
      setUnreadCount(0)
      setNotifications([])
    } catch { /* ignore */ }
  }

  const getLink = (notif) => {
    if (notif.type === 'order') return `/admin/orders/${notif.relatedId}`
    if (notif.type === 'send') return `/admin/sends/${notif.relatedId}`
    if (notif.type === 'ticket') return `/admin/tickets/${notif.relatedId}`
    if (notif.type === 'chat') return `/admin/chat/${notif.relatedId}`
    return '#'
  }

  const getTypeBadge = (type) => {
    if (type === 'order') return <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#3b82f6', background: '#3b82f620', padding: '0.125rem 0.375rem', borderRadius: '4px' }}>ORDER</span>
    if (type === 'send') return <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#10b981', background: '#10b98120', padding: '0.125rem 0.375rem', borderRadius: '4px' }}>SEND</span>
    if (type === 'ticket') return <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#f97316', background: '#f9731620', padding: '0.125rem 0.375rem', borderRadius: '4px' }}>TICKET</span>
    return null
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn btn-outline btn-sm"
        onClick={() => { setDropdownOpen(!dropdownOpen); requestNotifPermission() }}
        title={t('notificationBell.title')}
        style={{ position: 'relative', padding: '0.375rem', lineHeight: 0 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: '#ef4444',
            color: '#fff',
            fontSize: '0.625rem',
            fontWeight: 700,
            minWidth: '16px',
            height: '16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            padding: '0 3px',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {dropdownOpen && (
        <div style={{
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? '3.5rem' : '100%',
          left: isMobile ? '1rem' : 'auto',
          right: isMobile ? '1rem' : 0,
          marginTop: isMobile ? 0 : '0.5rem',
          width: isMobile ? 'auto' : '360px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 200,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{t('notificationBell.title')}</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem' }}
              >
                {t('notificationBell.markAllRead')}
              </button>
            )}
          </div>

          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {unreadCount > 0 ? t('notificationBell.loading') : t('notificationBell.noNew')}
              </div>
            ) : (
              notifications.map(n => (
                <div key={n.objectId} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontSize: '0.8125rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ marginBottom: '0.25rem' }}>{getTypeBadge(n.type)}</div>
                    <Link to={getLink(n)} style={{ color: 'var(--text)', textDecoration: 'none', display: 'block', wordBreak: 'break-word' }} onClick={() => setDropdownOpen(false)}>
                      {n.message}
                    </Link>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.6875rem', marginTop: '0.125rem' }}>
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => handleMarkRead(n.objectId)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0.25rem', lineHeight: 0, flexShrink: 0 }}
                    title="Mark as read"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>

          <Link
            to={adminLink('/admin/notifications')}
            onClick={() => setDropdownOpen(false)}
            style={{ display: 'block', padding: '0.75rem 1rem', textAlign: 'center', borderTop: '1px solid var(--border)', color: 'var(--primary)', textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 500 }}
          >
            {t('notificationBell.viewAll')}
          </Link>
        </div>
      )}
    </div>
  )
}
